/** CloudWatch RUM bootstrap and bounded custom-event queue for dashboard activation. */
import type { AutheEnv } from "@authe/fe-commons/env";

type AwsRumCtor = new (
  applicationId: string,
  applicationVersion: string,
  applicationRegion: string,
  config: Record<string, unknown>,
) => unknown;

/** The slice of `AwsRum` custom events need. */
export interface RumEventClient {
  recordEvent(eventType: string, eventData: object): void;
}

export interface RumEventRecorder {
  record(eventType: string, eventData: Record<string, unknown>): void;
  /** RUM started: flush what waited. A client without `recordEvent` turns recording off. */
  ready(client: unknown): void;
  /** RUM is not configured or failed: drop what waited, record nothing. */
  disable(): void;
}

/** Events kept while the RUM chunk loads; later ones are dropped, never unbounded. */
export const MAX_PENDING_RUM_EVENTS = 20;

function isRumEventClient(value: unknown): value is RumEventClient {
  return typeof (value as { recordEvent?: unknown } | null)?.recordEvent === "function";
}

export function createRumEventRecorder(): RumEventRecorder {
  let client: RumEventClient | null = null;
  let off = false;
  const pending: Array<[string, Record<string, unknown>]> = [];
  const send = (eventType: string, eventData: Record<string, unknown>): void => {
    try {
      client?.recordEvent(eventType, eventData);
    } catch {
      // Telemetry must never break the page.
    }
  };
  const disable = (): void => {
    off = true;
    client = null;
    pending.length = 0;
  };
  return {
    record(eventType, eventData) {
      if (off) return;
      if (client) send(eventType, eventData);
      else if (pending.length < MAX_PENDING_RUM_EVENTS) pending.push([eventType, eventData]);
    },
    ready(next) {
      if (off) return;
      if (!isRumEventClient(next)) {
        disable();
        return;
      }
      client = next;
      for (const [eventType, eventData] of pending.splice(0)) send(eventType, eventData);
    },
    disable,
  };
}

const rumEvents = createRumEventRecorder();

/** Record a custom RUM event (no PII). No-op when RUM is off. */
export function recordRumEvent(eventType: string, eventData: Record<string, unknown>): void {
  rumEvents.record(eventType, eventData);
}

let started = false;

/** Initialise CloudWatch RUM once, if the RUM env ids are configured. */
export async function initRum(env: AutheEnv): Promise<void> {
  if (started) return;
  if (!env.rumAppId || !env.rumIdentityPoolId || !env.rumRegion) {
    rumEvents.disable();
    return;
  }
  started = true;

  try {
    // Literal specifier → Vite bundles aws-rum-web into its own lazy chunk
    // (unlike fe-commons' @vite-ignore variable import, which stays unbundled).
    const { AwsRum } = (await import("aws-rum-web")) as unknown as { AwsRum: AwsRumCtor };
    const rum = new AwsRum(env.rumAppId, "1.0.0", env.rumRegion, {
      sessionSampleRate: 1,
      identityPoolId: env.rumIdentityPoolId,
      endpoint: `https://dataplane.rum.${env.rumRegion}.amazonaws.com`,
      telemetries: ["errors", "performance", "http"],
      allowCookies: true,
      enableXRay: false,
    });
    rumEvents.ready(rum);
  } catch {
    // RUM is best-effort telemetry — never let it break the app.
    started = false;
    rumEvents.disable();
  }
}
